import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getParamsFromUrl, isEntryFlow } from '../../utilities/urlResolver'
import { getInitialV2State } from './wizardService'
import { isMobile } from '../../utilities/helpers'
import abTesting from '../../services/abTesting'
import { LOCALE } from '../../constants'

export const getWizardLandingStep = () => {
  if (
    (
      abTesting.wizardLandingStep === 'who' ||
      abTesting.wizardLandingStep === 'who2' ||
      abTesting.wizardLandingStepAlternative ||
      abTesting.aiAssistant ||
      (abTesting.aiAssistantV2 && !isMobile())
    ) &&
    !isEntryFlow('link-presets-welcome')
  ) {
    if (abTesting.aiAssistant) {
      const wasAIAssistantShown = localStorage.getItem('wasAIAssistantShown') === 'true';
      return (wasAIAssistantShown || !isMobile()) ? 'wizardTypeSelector' : 'assistant';
    }
    if (!isMobile() && (abTesting.aiAssistant || abTesting.aiAssistantV2)) {
      return 'wizardTypeSelector';
    }
    return 'who';
  }

  if (isMobile()) {
    if (window.location.href.includes('startWizard=true')) {
      return 'who';
    }
    return 'welcome';
  }

  return 'who';
}

const initialState = {
  step: 0,
  currentStep: getWizardLandingStep(),
  show: false,
  showDesktop: false,
  showMobileHP: false,
  initiator: 'default',
  type: abTesting.aiAssistant ? 'assistant' : 'wizard', // 'wizard' | 'assistant'
  stepsConfig: {
    who: true,
    when: true,
    nights: true,
    experiences: true,
    budget: true,
    style: true,
    departure: true,
    email: false,
    showDestinationSelection: false,
    facilities: null,
  },
  formV2: getInitialV2State(),
  capacityTooltip: true,
  capacityScreens: false,
  stepNames: [],
  widgetStartSearch: false,
  userProfile: {},
  showHotelPreferences: false,
  dynamicSteps: [],
  dynamicStepsStatus: 'idle',
  dynamicAnswers: {},
  showAllQuestions: false,
  changeAnswersClicked: false,
}

export const fetchDynamicSteps = createAsyncThunk(
  'onboarding/fetchDynamicSteps',
  async (params: { profileId: string }, thunkAPI) => {
    try {
      const hfUser = JSON.parse(localStorage.getItem('hf_user') || '');
      const response = await fetch(`/api_no_auth/holiday_finder/user/questions?search_profile_id=${params.profileId}&muid=${hfUser.user?.muid}&locale=${LOCALE}&v2=true`);

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
)

export const sendDynamicAnswers = createAsyncThunk(
  'onboarding/setAnswer',
  async (params, thunkAPI) => {
    const state = thunkAPI.getState() as { onboarding: typeof initialState };
    const answers = state.onboarding.dynamicAnswers;
    const searchProfileId = state.user.searchProfile?.id;
    const hfUser = JSON.parse(localStorage.getItem('hf_user') || '');
    const body = {
      muid: hfUser.user?.muid,
      search_profile_id: searchProfileId,
      answers,
      isChanging: state.onboarding.changeAnswersClicked,
      locale: LOCALE,
    }
    // ${window.apiV6Config.path}/user/submitAnswers
    // http://v6-feat-userPersonalisation.int.stagewearemove.com/api/v2/user/submitAnswers
    return await fetch(`${window.apiV6Config.path}/user/submitAnswers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
    });
  }
)

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    updateValue: (state, action) => {
      return {
        ...state,
        ...action.payload
      }
    },
    setDataV2: (state, action) => {
      state.formV2 = {
        ...state.formV2,
        ...action.payload
      }
    },
    setStep: (state, action) => {
      state.step = action.payload
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      const direction = action.payload < 0 ? 'prev' : 'next';
      if (direction === 'next') {
        const currentStepIndex = state.stepNames.findIndex(step => step === state.currentStep);
        const nextStep = currentStepIndex >= 0 ? state.stepNames[currentStepIndex + 1] : state.stepNames[0];
        state.currentStep = nextStep;
      } else {
        const currentStepIndex = state.stepNames.findIndex(step => step === state.currentStep);
        const prevStep = state.stepNames[currentStepIndex - 1];
        state.currentStep = prevStep;
      }
    },
    setStepName: (state, action) => {
      state.currentStep = action.payload;
    },
    clearStepsProgress: (state) => {
      state.currentStep = getWizardLandingStep(); // isMobile() ? getWizardLandingStep() : 'who';
    },
    setStepNames: (state, action) => {
      state.stepNames = action.payload
    },
    hideOnboarding: (state) => {
      state.show = false
    },
    showOnboarding: (state, action) => {
      state.show = true;
      state.initiator = action.payload?.initiator || 'default';
    },
    setVisibleSteps: (state, action) => ({
      ...state,
      show: window.innerWidth <= 768 && (Object.keys(getParamsFromUrl()).length <= 1 || isEntryFlow() || isEntryFlow('link-presets-welcome')),
      stepsConfig: {
        ...state.stepsConfig,
        ...action.payload
      }
    }),
    setCapacityTooltip: (state, action) => {
      state.capacityTooltip = action.payload;
    },
    setCapacityScreens: (state, action) => {
      state.capacityScreens = action.payload;
    },
    setWizardDesktop: (state, action) => {
      state.showDesktop = action.payload;
    },
    setMobileHomepage: (state, action) => {
      state.showMobileHP = action.payload;
    },
    startOverWizard: (state) => {
      state.showDesktop = true;
      state.step = 0;
      state.currentStep = getWizardLandingStep(); // isMobile() ? getWizardLandingStep() : 'who';
      state.formV2 = {
        ...getInitialV2State(),
        who: state.formV2.who,
      };
      if(!isMobile()) {
        state.show = true;
      }
    },
    setWizardType: (state, action) => {
      state.type = action.payload;
      state.currentStep = action.payload === 'assistant' ? 'assistant' : 'who';
    },
    setWidgetStartSearch: (state, action) => {
      state.widgetStartSearch = action.payload;
    },
    setDynamicAnswer: (state, action) => {
      state.dynamicAnswers = {
        ...state.dynamicAnswers,
        [action.payload.id]: action.payload.answer,
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDynamicSteps.fulfilled, (state, action) => {
        const sortedSteps = action.payload.sort((a, b) => b.priority - a.priority);
        state.dynamicSteps = sortedSteps;
        state.dynamicStepsStatus = 'fulfilled';
      })
      .addCase(fetchDynamicSteps.pending, (state) => {
        state.dynamicStepsStatus = 'pending';
      })
      .addCase(fetchDynamicSteps.rejected, (state) => {
        state.dynamicSteps = [];
        state.dynamicStepsStatus = 'rejected';
      })
  }
})

export const {
  updateValue,
  setDataV2,
  setStep,
  showOnboarding,
  hideOnboarding,
  setVisibleSteps,
  setCapacityTooltip,
  setCapacityScreens,
  setWizardDesktop,
  setMobileHomepage,
  startOverWizard,
  setStepNames,
  setStepName,
  setCurrentStep,
  clearStepsProgress,
  setWizardType,
  setWidgetStartSearch,
  setDynamicAnswer,
} = onboardingSlice.actions;

export default onboardingSlice.reducer
